// Merged cells store their value only at the top-left anchor. Invalid ranges
// are ignored without deleting any cells, including nonempty covered cells.
export function normalizeMergedCells(ranges, rows) {
  const result = []; const occupied = new Set();
  if (!Array.isArray(ranges)) return result;
  for (const range of ranges.slice(0, 10000)) {
    if (!range || !['r1', 'c1', 'r2', 'c2'].every(key => Number.isInteger(range[key]))) continue;
    const { r1, c1, r2, c2 } = range;
    if (r1 < 0 || c1 < 0 || r2 < r1 || c2 < c1 || r2 >= rows.length
      || c2 >= (rows[0]?.length || 0) || (r1 === r2 && c1 === c2)) continue;
    const keys = []; let valid = true;
    for (let r = r1; r <= r2 && valid; r++) for (let c = c1; c <= c2; c++) {
      const key = `${r}:${c}`; const cell = rows[r]?.[c];
      if (occupied.has(key) || !cell || ((r !== r1 || c !== c1)
        && (cell.f || (cell.v !== '' && cell.v != null)))) { valid = false; break; }
      keys.push(key);
    }
    if (!valid) continue;
    keys.forEach(key => occupied.add(key)); result.push({ r1, c1, r2, c2 });
  }
  return result.sort((a, b) => a.r1 - b.r1 || a.c1 - b.c1);
}

export function createMergeIndex(ranges = []) {
  const index = new Map();
  ranges.forEach(range => {
    for (let r = range.r1; r <= range.r2; r++) for (let c = range.c1; c <= range.c2; c++) index.set(`${r}:${c}`, range);
  });
  return index;
}
export const mergeAt = (index, r, c) => index.get(`${r}:${c}`);
export function mergedAnchor(index, r, c) {
  const range = mergeAt(index, r, c);
  return range ? { r: range.r1, c: range.c1 } : { r, c };
}
export function moveMergedSelection(index, current, dr, dc, rowCount, colCount) {
  const anchor = mergedAnchor(index, current.r, current.c);
  const range = mergeAt(index, anchor.r, anchor.c);
  const r = (range && dr > 0 ? range.r2 : anchor.r) + dr;
  const c = (range && dc > 0 ? range.c2 : anchor.c) + dc;
  return mergedAnchor(index, Math.max(0, Math.min(rowCount - 1, r)), Math.max(0, Math.min(colCount - 1, c)));
}
export const rangesIntersect = (a, b) => a.r1 <= b.r2 && a.r2 >= b.r1 && a.c1 <= b.c2 && a.c2 >= b.c1;
export function expandMergedSelection(bounds, ranges = []) {
  const next = { ...bounds }; let changed;
  do {
    changed = false;
    ranges.forEach(range => {
      if (!rangesIntersect(next, range)) return;
      const expanded = { r1: Math.min(next.r1, range.r1), c1: Math.min(next.c1, range.c1), r2: Math.max(next.r2, range.r2), c2: Math.max(next.c2, range.c2) };
      if (Object.keys(next).some(key => next[key] !== expanded[key])) { Object.assign(next, expanded); changed = true; }
    });
  } while (changed);
  return next;
}

// Insertion inside a merge expands it; deletion contracts it. Deleting an
// entire span removes the merge. Cell values follow the editor's row/column edit.
export function shiftMergedCells(ranges = [], axis, at, delta) {
  const lo = axis === 'row' ? 'r1' : 'c1'; const hi = axis === 'row' ? 'r2' : 'c2';
  return ranges.flatMap(range => {
    const next = { ...range };
    if (delta > 0) {
      if (at <= next[lo]) { next[lo]++; next[hi]++; }
      else if (at <= next[hi]) next[hi]++;
    } else {
      if (at < next[lo]) { next[lo]--; next[hi]--; }
      else if (at <= next[hi]) { if (next[lo] === next[hi]) return []; next[hi]--; }
    }
    return next.r1 === next.r2 && next.c1 === next.c2 ? [] : [next];
  });
}
